import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { navigate } from '../../../../../../navigation/RootNavigation';

const titleOptions = ['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof', 'Sir', 'Dame', 'N/A'];

const getDisplayValue = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return value || '';
};

const getLandlordName = (landlord) => {
  const firstName = landlord?.first_name || '';
  const lastName = landlord?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || landlord?.email || landlord?.open_flow_landlord_id || 'Unknown landlord';
};

const getLandlordLabel = (landlord) => {
  const title = landlord?.title ? `${landlord.title}. ` : '';
  return `${title}${getLandlordName(landlord)}`.trim();
};

export const isLandlordTitleMissing = (landlord) => {
  const title = landlord?.title;
  return typeof title !== 'string' || !title.trim();
};

const formatLandlord = (landlord) => ({
  id: String(landlord?.open_flow_landlord_id || landlord?.email || ''),
  title: landlord?.title || '',
  firstName: landlord?.first_name || '',
  lastName: landlord?.last_name || '',
  email: landlord?.email || '',
  phone: landlord?.number || '',
  licenseNumber: landlord?.license_number || '',
  address: landlord?.correspondece_address || '',
  postcode: landlord?.post_code || '',
  isRequiredCountryAddress: getDisplayValue(landlord?.is_england_address),
  alternativeEmail: landlord?.email_england || '',
  alternativePhone: landlord?.number_england || '',
  alternativePostcode: landlord?.post_code_england || '',
  alternativeAddress: landlord?.correspondece_address_england || '',
});

const LandlordDetails = ({
  openFlow,
  showMissingTitlePopup: externalShowPopup,
  setShowMissingTitlePopup: externalSetShowPopup,
}) => {
  const landlords = Array.isArray(openFlow?.open_flow_landlords) ? openFlow.open_flow_landlords : [];
  const [formData, setFormData] = useState({});
  const [selectedLandlordId, setSelectedLandlordId] = useState('');
  const [internalShowPopup, setInternalShowPopup] = useState(false);

  const showMissingTitlePopup = externalShowPopup !== undefined ? externalShowPopup : internalShowPopup;
  const setShowMissingTitlePopup = externalSetShowPopup || setInternalShowPopup;

  useEffect(() => {
    if (landlords.length > 0) {
      const formatted = {};
      landlords.forEach((landlord, index) => {
        const key = String(landlord?.open_flow_landlord_id || landlord?.email || index);
        formatted[key] = formatLandlord({ ...landlord, open_flow_landlord_id: key });
      });

      setFormData(formatted);
      setSelectedLandlordId(String(landlords[0]?.open_flow_landlord_id || landlords[0]?.email || 0));
    }
  }, [openFlow?.open_flow_landlords]);

  const currentData = selectedLandlordId ? formData[selectedLandlordId] : null;

  const selectedIndex = landlords.findIndex(
    (l, idx) => String(l?.open_flow_landlord_id || l?.email || idx) === selectedLandlordId
  );
  const displayIndex = selectedIndex !== -1 ? selectedIndex + 1 : 1;

  const missingTitleLandlords = landlords
    .filter(isLandlordTitleMissing)
    .map(getLandlordName);

  const handleCardClick = (landlord, index) => {
    setSelectedLandlordId(String(landlord?.open_flow_landlord_id || landlord?.email || index));
  };

  const handleGoToLandlords = () => {
    setShowMissingTitlePopup(false);
    navigate('PropertyDocumentation', { id: openFlow?.openflow_id });
  };

  if (!landlords.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          No landlords found. Please add landlord information before completing deposit protection.
        </Alert>
        <Box sx={{ mt: 3 }}>
          <Button onClick={handleGoToLandlords} variant="contained" color="primary">
            Go to landlords
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, bgcolor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#1E293B', fontSize: 18 }}>
            Landlord Details {displayIndex} of {landlords.length}
          </Typography>
          <Button
            onClick={handleGoToLandlords}
            variant="text"
            sx={{ color: '#1976D2', textTransform: 'none', fontWeight: 600 }}
          >
            Edit landlord info
          </Button>
        </Box>

        {/* Scrollable Landlord Cards */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            overflowY: 'hidden',
            flexWrap: 'nowrap',
            paddingBottom: 1.5,
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#CBD5E1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#F1F5F9',
            },
          }}
        >
          {landlords.map((landlord, index) => {
            const landlordId = String(landlord?.open_flow_landlord_id || landlord?.email || index);
            const missingTitle = isLandlordTitleMissing(landlord);
            const isSelected = selectedLandlordId === landlordId;

            return (
              <Box
                key={landlordId}
                sx={{
                  minWidth: landlords.length <= 2 ? { xs: '100%', sm: '48%' } : '250px',
                  flexShrink: 0,
                }}
              >
                <Card
                  variant="outlined"
                  onClick={() => handleCardClick(landlord, index)}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderColor: isSelected ? '#1976D2' : '#E2E8F0',
                    borderWidth: 2,
                    borderRadius: 2,
                    bgcolor: isSelected ? '#F8FAFC' : '#FFFFFF',
                    transition: 'all 0.2s ease',
                    boxShadow: isSelected ? '0 0 0 1px #1976D2' : 'none',
                  }}
                >
                  <MaterialCommunityIcons
                    name="account-tie"
                    size={32}
                    color="#666CFF"
                  />
                  <Box ml={2} sx={{ flex: 1, minWidth: 0 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={600} sx={{ color: '#1E293B', fontSize: 14 }}>
                        {landlord?.is_lead ? 'Lead Landlord' : 'Joint Landlord'}
                      </Typography>
                      {missingTitle && (
                        <Tooltip title="Landlord title is missing">
                          <Box component="span" display="flex">
                            <MaterialCommunityIcons name="alert-circle" size={18} color="#FF4D49" />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        color: '#64748B',
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {getLandlordLabel(landlord)}
                    </Typography>
                  </Box>
                </Card>
              </Box>
            );
          })}
        </Box>

        {/* Selected Landlord Form Fields */}
        {currentData && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Title</InputLabel>
                <Select disabled value={currentData.title || ''} label="Title">
                  <MenuItem value="">N/A</MenuItem>
                  {titleOptions.map((title) => (
                    <MenuItem key={title} value={title}>
                      {title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                fullWidth
                size="small"
                value={currentData.firstName}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Surname"
                fullWidth
                size="small"
                value={currentData.lastName}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                fullWidth
                size="small"
                value={currentData.email}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Number"
                fullWidth
                size="small"
                value={currentData.phone}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Landlord Registration Number"
                fullWidth
                size="small"
                value={currentData.licenseNumber}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Postcode"
                fullWidth
                size="small"
                value={currentData.postcode}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                size="small"
                value={currentData.address}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Address in Required Country"
                fullWidth
                size="small"
                value={currentData.isRequiredCountryAddress}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Alternative Email"
                fullWidth
                size="small"
                value={currentData.alternativeEmail}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Alternative Contact Number"
                fullWidth
                size="small"
                value={currentData.alternativePhone}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Alternative Postcode"
                fullWidth
                size="small"
                value={currentData.alternativePostcode}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Alternative Address"
                fullWidth
                size="small"
                value={currentData.alternativeAddress}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Missing Title Dialog */}
      <Dialog
        open={showMissingTitlePopup}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setShowMissingTitlePopup(false);
          }
        }}
      >
        <DialogTitle>Missing Info. Landlord Title</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The title field of a landlord is missing. Please add the title of the following landlord:{' '}
            &quot;{missingTitleLandlords.join(', ')}&quot;.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              Landlord title is mandatory before continuing with deposit protection.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMissingTitlePopup(false)} color="secondary" variant="outlined">
            Go back
          </Button>
          <Button onClick={handleGoToLandlords} variant="contained" color="primary">
            Go to landlords
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandlordDetails;
